<template>
  <modal
    elem-id="stockProductModal"
    data-testid="stock-product-modal"
    :show="isVisible"
    :title="modalTitle"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="t('Salvar')"
    save-test-id="stock-product-save-btn"
    :save-disabled-fn="() => isSaving || isReadOnly || !formData.so_product_name"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <!-- Read-only banner -->
      <div v-if="isReadOnly" class="readonly-banner" data-testid="stock-product-readonly-banner">
        <i class="pi pi-info-circle"></i>
        <span>{{ readonlyBannerText }}</span>
      </div>

      <div class="form-field">
        <input-text
          elem-id="stockCreateProductName"
          v-model="formData.so_product_name"
          :label="t('Nome do Produto')"
          :placeholder="t('Digite o nome do produto')"
          :disabled="isReadOnly"
          required
          fluid
          data-testid="stock-product-name"
        />
      </div>
      <div class="form-field">
        <input-currency
          elem-id="stockCreateProductPrice"
          v-model="formData.so_product_default_price"
          :label="t('Preço')"
          :placeholder="t('R$ 0,00')"
          :disabled="isReadOnly"
          fluid
          data-testid="stock-product-price"
        />
      </div>

      <!-- Category selector -->
      <div class="form-field" v-if="!isCreatingCategory">
        <div class="category-row">
          <select-list
          elem-id="stockCreateProductCategory"
            v-model="formData.so_product_category_id"
            :label="t('Categoria')"
            :itens-select="categoryOptions"
            :placeholder="t('Selecione uma categoria')"
            :disabled="isReadOnly"
            data-testid="stock-product-category"
          />
          <Button
            v-if="!isReadOnly"
            icon="pi pi-plus"
            severity="secondary"
            text
            size="small"
            class="add-cat-btn"
            v-tooltip.top="t('Nova Categoria')"
            data-testid="stock-new-category-add"
            @click="startCreateCategory"
          />
        </div>
      </div>
      <div class="form-field" v-if="isCreatingCategory">
        <label class="input-label">{{ t('Nova Categoria') }}</label>
        <div class="category-create-row">
          <input-text
            elem-id="stockNewCategoryName"
            v-model="newCategoryName"
            :placeholder="t('Digite o nome da categoria')"
            fluid
            data-testid="stock-new-category-input"
          />
          <Button
            icon="pi pi-check"
            severity="success"
            text
            size="small"
            :disabled="!newCategoryName.trim()"
            data-testid="stock-new-category-confirm"
            @click="confirmCreateCategory"
          />
          <Button
            icon="pi pi-times"
            severity="danger"
            text
            size="small"
            data-testid="stock-new-category-cancel"
            @click="cancelCreateCategory"
          />
        </div>
      </div>

      <!-- Franchise — edit mode shows product's franchise (immutable) -->
      <div class="form-field" v-if="currentProductId">
        <label class="input-label">{{ t('Franquia') }}</label>
        <div class="franchise-display">
          <i class="pi pi-building"></i>
          <span>{{ editModeFranchiseName }}</span>
        </div>
      </div>
      <!-- Franchise — create mode: scope + franchise selector -->
      <template v-else>
        <!-- Scope selector (Full users only) -->
        <div class="form-field" v-if="isFullUser">
          <select-list
            elem-id="stockProductScope"
            v-model="formData.so_product_scope"
            :label="t('Tipo de produto')"
            :itens-select="[
              { ID: 'global', Name: t('Catálogo global (todas as franquias)') },
              { ID: 'franchise', Name: t('Produto de uma franquia') }
            ]"
            data-testid="stock-product-scope"
          />
        </div>

        <!-- Franchise selector (shown for Full users when scope='franchise', or for non-Full users) -->
        <div class="form-field" v-if="(isFullUser && formData.so_product_scope === 'franchise') || !isFullUser">
          <select-list
            elem-id="stockProductFranchise"
            v-model="formData.so_product_franchise_id"
            :label="t('Franquia')"
            :itens-select="franchiseOptions"
            :placeholder="t('Selecione uma franquia')"
            data-testid="stock-product-franchise"
          />
        </div>
      </template>

      <!-- Image gallery launcher — only visible for existing products -->
      <div class="form-field" v-if="currentProductId">
        <Button
          :label="t('Gerenciar imagens')"
          icon="pi pi-images"
          severity="secondary"
          size="small"
          :disabled="isReadOnly"
          data-testid="stock-product-images-btn"
          @click="openImageGallery"
        />
      </div>

      <!-- Delete — only visible for existing products, disabled in read-only -->
      <div class="form-field delete-row" v-if="currentProductId && !isReadOnly">
        <Button
          :label="t('Excluir Produto')"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          outlined
          data-testid="stock-product-delete-btn"
          @click="handleDelete"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { permissionsService } from '../../services/permissionsService';
import { useFiltro } from '../../composables/useFiltro';
import { useSOProducts } from '../../composables/useSOProducts';

type Id = number | string;

interface SelectOption {
  ID: Id;
  Name: string;
}

const { t } = useI18n();
const { selectedFranchises } = useFiltro();
const { saveProduct, fetchProductCategories, saveProductCategory, getProduct, removeProduct } = useSOProducts();

const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const isCreatingCategory = ref<boolean>(false);
const newCategoryName = ref<string>('');
const categoryOptions = ref<SelectOption[]>([]);
const currentProductId = ref<number | null>(null);
const loadedProductFranchiseId = ref<number | null | undefined>(undefined);

const formData = reactive({
  so_product_name: '',
  so_product_category_id: undefined as number | undefined,
  so_product_default_price: 0,
  so_product_franchise_id: undefined as number | undefined,
  so_product_scope: 'global' as 'global' | 'franchise'
});

// Edit mode computed: modal title
const modalTitle = computed(() => {
  return currentProductId.value ? t('Editar Produto') : t('Novo Produto');
});

// Edit mode computed: product's franchise name (for display in edit mode)
const editModeFranchiseName = computed(() => {
  if (!currentProductId.value) return '';
  const fid = loadedProductFranchiseId.value;
  if (fid === null || fid === undefined) return t('Global');
  const name = permissionsService.getFranchiseName(fid);
  return name || String(fid);
});

// Permission gating
const isFullUser = computed(() => permissionsService.isUserInGroups(['Full']));

const isReadOnly = computed(() => {
  if (!currentProductId.value) return false; // Create mode: always editable
  const fid = loadedProductFranchiseId.value;
  if (fid === undefined) return false; // Not yet loaded
  if (fid === null) {
    // Global product: only Full users can edit
    return !isFullUser.value;
  }
  // Franchise product: check if user has access
  if (isFullUser.value) return false;
  return !permissionsService.canAccessFranchise(fid);
});

const readonlyBannerText = computed(() => {
  if (loadedProductFranchiseId.value === null) {
    return t('Você está visualizando um produto global. Para alterar o preço para esta franquia, use Ajustar Estoque.');
  }
  return t('Você não tem permissão para editar este produto.');
});

// Load categories from API
const loadCategories = async (): Promise<void> => {
  try {
    const response = await fetchProductCategories();
    if (response.success && Array.isArray(response.dados)) {
      categoryOptions.value = (response.dados as Array<{ value: unknown; label: unknown }>).map((item) => ({
        ID: Number(item.value),
        Name: String(item.label)
      }));
    }
  } catch (err) {
    logError('SOStockCreateModal', 'loadCategories', 'Error', err);
    categoryOptions.value = [];
  }
};

const startCreateCategory = (): void => {
  isCreatingCategory.value = true;
  newCategoryName.value = '';
  nextTick(() => {
    const input = document.querySelector<HTMLInputElement>('[data-testid="stock-new-category-input"] input');
    input?.focus();
  });
};

const cancelCreateCategory = (): void => {
  isCreatingCategory.value = false;
  newCategoryName.value = '';
};

const confirmCreateCategory = async (): Promise<void> => {
  const name = newCategoryName.value.trim();
  if (!name) return;

  try {
    const payload: Record<string, unknown> = { so_product_category_name: name };
    const fid = detectedFranchiseId.value;
    if (fid) {
      payload.so_product_category_franchise_id = Number(fid);
    }

    const result = await saveProductCategory(payload);
    log('SOStockCreateModal', 'confirmCreateCategory', 'Result', result);
    if (result.success) {
      const newId = (result.dados as { so_product_category_id?: number })?.so_product_category_id;
      if (newId) {
        categoryOptions.value.push({ ID: Number(newId), Name: name });
        formData.so_product_category_id = Number(newId);
      }
      eventBus.emit('globalMessage.success', t('Categoria criada'));
    } else {
      const errMsg = result.error || 'Erro desconhecido';
      logError('SOStockCreateModal', 'confirmCreateCategory', 'API error', errMsg);
      eventBus.emit('globalMessage.error', t('Erro ao criar categoria') + ': ' + errMsg);
    }
  } catch (err) {
    logError('SOStockCreateModal', 'confirmCreateCategory', 'Error', err);
    const msg = err instanceof Error ? err.message : String(err);
    eventBus.emit('globalMessage.error', t('Erro ao criar categoria') + ': ' + msg);
  } finally {
    isCreatingCategory.value = false;
    newCategoryName.value = '';
  }
};

// Load product data for edit mode
const loadProductForEdit = async (productId: number): Promise<void> => {
  try {
    const dados = await getProduct(productId);
    const product = dados.product;
    formData.so_product_name = String(product.so_product_name ?? '');
    formData.so_product_category_id = product.so_product_category_id !== null && product.so_product_category_id !== undefined
      ? Number(product.so_product_category_id)
      : undefined;
    formData.so_product_default_price = Number(product.so_product_default_price ?? 0);
    formData.so_product_franchise_id = product.so_product_franchise_id !== null && product.so_product_franchise_id !== undefined
      ? Number(product.so_product_franchise_id)
      : undefined;
    loadedProductFranchiseId.value = product.so_product_franchise_id !== null && product.so_product_franchise_id !== undefined
      ? Number(product.so_product_franchise_id)
      : null;
  } catch (err) {
    logError('SOStockCreateModal', 'loadProductForEdit', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar produto'));
    handleClose();
  }
};

// Franchise options for the fallback selector
const franchiseOptions = computed<SelectOption[]>(() => {
  try {
    return permissionsService.getFranquiasSelectArray()
      .map((item: { ID: string | number; Name: string }) => ({ ID: item.ID, Name: item.Name }))
      .sort((a: SelectOption, b: SelectOption) => a.Name.localeCompare(b.Name));
  } catch {
    return [];
  }
});

// Detect franchise from the global filter
const detectedFranchise = computed<{ id: string; name: string } | null>(() => {
  const list = selectedFranchises.value as { id?: string; name?: string }[] | undefined;
  if (list && list.length === 1) {
    return { id: String(list[0].id ?? ''), name: list[0].name ?? '' };
  }
  return null;
});

const detectedFranchiseName = computed<string>(() => detectedFranchise.value?.name || '');

const detectedFranchiseId = computed<string | undefined>(() => detectedFranchise.value?.id);

const resetForm = (): void => {
  formData.so_product_name = '';
  formData.so_product_category_id = undefined;
  formData.so_product_default_price = 0;
  formData.so_product_franchise_id = undefined;
  formData.so_product_scope = 'global';
  isCreatingCategory.value = false;
  newCategoryName.value = '';
  currentProductId.value = null;
  loadedProductFranchiseId.value = undefined;
};

const handleDelete = async (): Promise<void> => {
  if (!currentProductId.value) return;
  if (!window.confirm(t('Tem certeza que deseja excluir este produto?'))) return;
  isSaving.value = true;
  try {
    await removeProduct(currentProductId.value);
    eventBus.emit('globalMessage.success', t('Produto excluido com sucesso'));
    eventBus.emit('so-stock.adjusted');
    handleClose();
  } catch (err) {
    logError('SOStockCreateModal', 'handleDelete', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao excluir produto'));
  } finally {
    isSaving.value = false;
  }
};

const openImageGallery = (): void => {
  const productId = currentProductId.value;
  if (!productId) return;
  log('SOStockCreateModal', 'openImageGallery', 'Opening image gallery', { productId });
  eventBus.emit('modal.so-stock-image-gallery.open', { productId });
};

const handleSave = async (): Promise<void> => {
  if (!formData.so_product_name) return;
  isSaving.value = true;
  try {
    const payload: Record<string, unknown> = {
      so_product_name: formData.so_product_name.trim(),
      so_product_category_id: formData.so_product_category_id || 0,
      so_product_default_price: formData.so_product_default_price,
      so_product_unit_of_measure: 'un',
      so_product_track_stock: 1
    };

    // For create mode only: include franchise_id only if scope is 'franchise' or user is not Full
    if (!currentProductId.value) {
      const effectiveScope = isFullUser.value ? formData.so_product_scope : 'franchise';
      if (effectiveScope === 'franchise' && formData.so_product_franchise_id) {
        payload.so_product_franchise_id = formData.so_product_franchise_id;
      }
    } else {
      // Edit mode: preserve existing franchise_id logic
      if (formData.so_product_franchise_id) {
        payload.so_product_franchise_id = formData.so_product_franchise_id;
      }
    }

    // Include product ID for update mode
    if (currentProductId.value) {
      payload.so_product_id = currentProductId.value;
    }

    const result = await saveProduct(payload as any);

    if (currentProductId.value) {
      eventBus.emit('globalMessage.success', t('Produto atualizado com sucesso'));
    } else {
      const newProductId = (result.dados as { so_product_id?: unknown })?.so_product_id as number | undefined;
      if (newProductId) {
        currentProductId.value = Number(newProductId);
      }
      eventBus.emit('globalMessage.success', t('Produto criado com sucesso'));
    }

    eventBus.emit('so-stock.adjusted');
    handleClose();
  } catch (err) {
    logError('SOStockCreateModal', 'handleSave', 'Error', err);
    eventBus.emit('globalMessage.error',
      currentProductId.value ? t('Erro ao atualizar produto') : t('Erro ao criar produto'));
  } finally {
    isSaving.value = false;
  }
};

const openModal = async (payload?: { productId?: number | string } | unknown): Promise<void> => {
  isVisible.value = true;
  const incomingId = (payload as { productId?: number | string } | undefined)?.productId;
  if (incomingId === undefined || incomingId === null || incomingId === '') {
    // Create mode: initialize scope
    formData.so_product_scope = isFullUser.value ? 'global' : 'franchise';
    const fid = detectedFranchiseId.value;
    formData.so_product_franchise_id = fid ? Number(fid) : undefined;
    currentProductId.value = null;
    loadedProductFranchiseId.value = undefined;
  } else {
    // Edit mode
    currentProductId.value = Number(incomingId);
    loadedProductFranchiseId.value = undefined; // Will be set by loadProductForEdit
    await loadProductForEdit(Number(incomingId));
  }
  formData.so_product_default_price = 0;
  formData.so_product_name = '';
  formData.so_product_category_id = undefined;
  isCreatingCategory.value = false;
  newCategoryName.value = '';
  loadCategories();
};

const handleClose = (): void => {
  isVisible.value = false;
  resetForm();
};

onMounted(() => {
  eventBus.on('modal.so-stock-create.open', openModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-stock-create.open', openModal);
});
</script>

<style scoped>
.form-field {
  margin-bottom: 1rem;
}

.form-field :deep(.currency-input) {
  text-align: left;
}

.franchise-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--p-surface-100);
  border-radius: 6px;
  font-weight: 500;
  color: var(--p-primary-color);
}

.franchise-display i {
  font-size: 1.1rem;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  display: block;
  color: var(--p-text-color);
}

.category-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.category-row :deep(.select-list-wrapper) {
  flex: 1;
  margin-bottom: 0;
}

.category-row .add-cat-btn {
  background: var(--p-surface-100);
  border: 1px solid var(--p-surface-300);
  border-radius: 6px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.3rem;
}

.category-create-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.category-create-row :deep(.input-text-wrapper) {
  flex: 1;
  margin-bottom: 0;
}

.delete-row {
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.readonly-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: var(--p-yellow-50);
  border: 1px solid var(--p-yellow-200);
  border-radius: 6px;
  font-size: 0.85rem;
  color: var(--p-yellow-800);
  margin-bottom: 1rem;
  line-height: 1.4;
}

.readonly-banner i {
  font-size: 1rem;
  margin-top: 2px;
  flex-shrink: 0;
}
</style>
